"use client"
import useOneTrustOptInLoad from "@/app/(site)/_utils/_hooks/useOneTrustOptInLoad"
import { modalEvent, signUpEvent } from "@/lib/utils/tracking/gtmEvents"
import { useSiteContext } from "@/lib/utils/tracking/siteContext"
import Script from "next/script"
import { useEffect, useRef } from "react"
import { extractUserData } from "../utils/tracking/klaviyoForms"

export const KlaviyoScript = () => {
    // check if the user has given consent to the functional cookies
    const consent = useOneTrustOptInLoad()
    const eventHandlerRef = useRef<((e: any) => void) | null>(null)
    const siteContext = useSiteContext()
    const siteContextRef = useRef(siteContext)
    const lastEventRef = useRef<{ type: string; timestamp: number } | null>(null)

    // Update ref when siteContext changes
    useEffect(() => {
        siteContextRef.current = siteContext
    }, [siteContext])

    useEffect(() => {
        if (consent?.targetingConsent || consent?.socialMediaConsent) {
            // Track Klaviyo form events using official Klaviyo form events
            const trackKlaviyoForms = () => {
                const eventHandler = (e: any) => {
                    const now = Date.now()

                    // Prevent duplicate events within 100ms
                    if (
                        lastEventRef.current &&
                        lastEventRef.current.type === e.detail.type &&
                        now - lastEventRef.current.timestamp < 100
                    ) {
                        return
                    }

                    lastEventRef.current = { type: e.detail.type, timestamp: now }

                    if (e.detail.type === "open") {
                        // Track popup open
                        modalEvent(
                            "klaviyo-popup",
                            "Become a POC Member",
                            "open",
                            siteContextRef.current
                        )
                    } else if (e.detail.type === "close") {
                        // Track popup close
                        modalEvent(
                            "klaviyo-popup",
                            "Become a POC Member",
                            "close",
                            siteContextRef.current
                        )
                    } else if (e.detail.type === "submit") {
                        // Track form submission

                        // Determine signup method based on context
                        const determineSignupMethod = (
                            detail: any,
                            pathname: string
                        ): string => {
                            // Check if we're on checkout page
                            if (pathname.includes("/checkout")) {
                                return "Checkout"
                            }

                            // Check if this is a modal/popup form
                            if (detail.formId || detail.formType === "popup") {
                                return "Modal"
                            }

                            // Check if we're on footer area (common for newsletter signup)
                            if (pathname === "/" || pathname.endsWith("/")) {
                                return "Footer"
                            }

                            // Default fallback
                            return "Newsletter"
                        }

                        const userData = extractUserData(e.detail)
                        const signupMethod = determineSignupMethod(
                            e.detail,
                            window.location.pathname
                        )

                        // Only track if we have at least an email
                        if (userData.email) {
                            signUpEvent(signupMethod, siteContextRef.current, userData)
                        }
                    }
                }

                // Store the event handler in ref for cleanup
                eventHandlerRef.current = eventHandler
                window.addEventListener("klaviyoForms", eventHandler)
            }

            trackKlaviyoForms()
        }

        // Cleanup function to remove event listener
        return () => {
            if (eventHandlerRef.current) {
                window.removeEventListener("klaviyoForms", eventHandlerRef.current)
                eventHandlerRef.current = null
            }
        }
    }, [consent?.targetingConsent, consent?.socialMediaConsent])

    return consent?.targetingConsent || consent?.socialMediaConsent ? (
        <Script
            strategy="afterInteractive"
            src={`https://static.klaviyo.com/onsite/js/klaviyo.js?company_id=${process.env.NEXT_PUBLIC_KLAVIYO_ID}`}
            id={`klaviyo-script-${process.env.NEXT_PUBLIC_KLAVIYO_ID}`}
        />
    ) : null
}
